import { useMutation } from '@apollo/client';
import type { UpdateUserGroupMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateUserGroupDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateUserGroupTRPC } from './useUpdateUserGroupTRPC';

type UpdateUserGroupInput = {
  Id: string;
  OriginalTimestamp: string;
  Name?: string;
  Email?: string | null;
  Description?: string | null;
  OwnerContributor?: boolean;
};

export const useUpdateUserGroup = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateUserGroupGraphQL, graphqlState] = useMutation(
    UpdateUserGroupDocument,
    {
      update: (cache) => {
        evictField(cache, 'user_group');
        evictField(cache, 'user_group_user');
      },
    }
  );

  const trpcMutation = useUpdateUserGroupTRPC();

  const updateUserGroup = async (
    variables: UpdateUserGroupInput
  ): Promise<UpdateUserGroupMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateUserGroup(variables);
    }

    const result = await updateUserGroupGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update user group');
    }

    return result.data;
  };

  return {
    updateUserGroup,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
